import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, Eye, ExternalLink, Upload } from "lucide-react";
import { BlockRenderer, DEFAULTS } from "@/components/builder/BlockRenderer";
import RichTextEditor from "@/components/RichTextEditor";
import { HEADING_FONTS } from "@/lib/platformFonts";

// Block types available for platform pages.
// We omit salon-specific blocks (booking, services, team, newsletter, loyalty)
// because the platform context has no theme/staff data.
const PLATFORM_BLOCK_TYPES = [
  { type: "hero", label: "Hero" },
  { type: "heading", label: "Überschrift" },
  { type: "text", label: "Textblock" },
  { type: "image", label: "Bild" },
  { type: "image_fullwidth", label: "Bild Full-Width" },
  { type: "button", label: "Button" },
  { type: "testimonials", label: "Stimmen" },
  { type: "faq", label: "FAQ" },
  { type: "spacer", label: "Abstand" },
];

const newId = () => Math.random().toString(36).slice(2);

export default function PlatformPageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [platformTypography, setPlatformTypography] = useState({ heading_font: "Cormorant Garamond", body_font: "Manrope" });
  const [saving, setSaving] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/platform-pages/${id}`).then(({ data }) => setPage(data));
    api.get("/public/platform-content").then(({ data }) => {
      if (data?.typography) setPlatformTypography({
        heading_font: data.typography.heading_font || "Cormorant Garamond",
        body_font: data.typography.body_font || "Manrope",
      });
    }).catch(() => {});
  }, [id]);

  if (!page) return <div className="p-8">Lade…</div>;

  const setBlocks = (next) => setPage({ ...page, blocks: next });

  const addBlock = (type) => {
    const block = { id: newId(), type, props: JSON.parse(JSON.stringify(DEFAULTS[type] || {})) };
    setBlocks([...(page.blocks || []), block]);
    setSelectedIdx((page.blocks || []).length);
  };

  const removeBlock = (idx) => {
    const next = (page.blocks || []).filter((_, i) => i !== idx);
    setBlocks(next);
    setSelectedIdx(null);
  };

  const move = (idx, dir) => {
    const next = [...(page.blocks || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setBlocks(next);
    setSelectedIdx(j);
  };

  const updateBlock = (idx, patch) => {
    const next = [...(page.blocks || [])];
    next[idx] = { ...next[idx], props: { ...next[idx].props, ...patch } };
    setBlocks(next);
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.put(`/admin/platform-pages/${id}`, {
        slug: page.slug, title: page.title, blocks: page.blocks || [],
        seo: page.seo || null, published: !!page.published,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const selected = selectedIdx !== null ? (page.blocks || [])[selectedIdx] : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--mv-bg)" }}>
      {/* Top bar */}
      <header className="border-b" style={{ borderColor: "var(--mv-border)" }}>
        <div className="px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Link to="/admin/pages" className="mv-btn-ghost text-sm shrink-0" data-testid="back-to-list">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Zurück
            </Link>
            <div className="min-w-0">
              <input
                data-testid="page-title-input"
                className="text-lg font-display bg-transparent border-0 outline-none w-full"
                value={page.title}
                onChange={(e) => setPage({ ...page, title: e.target.value })}
                disabled={page.system}
                placeholder="Seitentitel"
              />
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--mv-text-muted)" }}>
                <span>/</span>
                <input
                  data-testid="page-slug-input"
                  className="font-mono bg-transparent border-0 outline-none"
                  value={page.slug}
                  onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  disabled={page.system}
                />
                <label className="ml-3 flex items-center gap-1">
                  <input type="checkbox" data-testid="page-published" checked={!!page.published} onChange={(e) => setPage({ ...page, published: e.target.checked })} />
                  veröffentlicht
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="mv-btn-ghost text-sm" data-testid="live-preview-link">
              <ExternalLink className="w-4 h-4" /> Live ansehen
            </a>
            <button onClick={save} disabled={saving} className="mv-btn" data-testid="save-page-btn">
              <Save className="w-4 h-4" /> {saving ? "…" : "Speichern"}
            </button>
          </div>
        </div>
        {err && <div className="px-6 py-2 text-xs text-red-700 bg-red-50">{err}</div>}
      </header>

      {/* Workspace */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] overflow-hidden">
        {/* Left: components + structure */}
        <aside className="border-r overflow-y-auto" style={{ borderColor: "var(--mv-border)" }}>
          <div className="p-4">
            <p className="mv-label mb-3">Komponenten</p>
            <div className="grid grid-cols-2 gap-1.5">
              {PLATFORM_BLOCK_TYPES.map((c) => (
                <button
                  key={c.type}
                  data-testid={`add-${c.type}`}
                  onClick={() => addBlock(c.type)}
                  className="text-left text-xs p-2 rounded border hover:bg-[var(--mv-surface)] transition-colors"
                  style={{ borderColor: "var(--mv-border)" }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <p className="mv-label mt-6 mb-3">Aufbau</p>
            <div className="space-y-1" data-testid="block-list">
              {(page.blocks || []).map((b, i) => {
                const active = selectedIdx === i;
                return (
                  <div key={b.id} className="flex items-center gap-1 group">
                    <button
                      data-testid={`select-block-${i}`}
                      onClick={() => setSelectedIdx(i)}
                      className="flex-1 text-left px-2 py-1.5 rounded text-xs"
                      style={{
                        background: active ? "var(--mv-primary)" : "transparent",
                        color: active ? "#fff" : "var(--mv-text)",
                      }}
                    >
                      {b.type}
                    </button>
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="opacity-0 group-hover:opacity-100 disabled:opacity-20"><ChevronUp className="w-3 h-3" strokeWidth={1.5} /></button>
                    <button onClick={() => move(i, +1)} disabled={i === (page.blocks || []).length - 1} className="opacity-0 group-hover:opacity-100 disabled:opacity-20"><ChevronDown className="w-3 h-3" strokeWidth={1.5} /></button>
                    <button onClick={() => removeBlock(i)} className="opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-3 h-3" strokeWidth={1.5} /></button>
                  </div>
                );
              })}
              {(page.blocks || []).length === 0 && (
                <p className="text-xs opacity-60 py-3">Noch keine Blöcke. Klicke links oben auf eine Komponente.</p>
              )}
            </div>
          </div>
        </aside>

        {/* Center: live inline preview */}
        <main className="overflow-y-auto bg-white">
          <div className="p-2 text-[11px] opacity-60 flex items-center gap-1 border-b" style={{ borderColor: "var(--mv-border)" }}>
            <Eye className="w-3 h-3" strokeWidth={1.5} /> Vorschau (Klick = Block auswählen)
          </div>
          <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
            {(page.blocks || []).map((b, i) => (
              <div
                key={b.id}
                onClick={() => setSelectedIdx(i)}
                className="relative cursor-pointer"
                style={{
                  outline: selectedIdx === i ? "2px solid var(--mv-primary)" : "1px dashed transparent",
                  outlineOffset: 4,
                  borderRadius: 8,
                }}
              >
                <BlockRenderer block={b} theme={{}} forms={[]} services={[]} staff={[]} />
              </div>
            ))}
            {(page.blocks || []).length === 0 && (
              <p className="text-center py-20 opacity-50">Diese Seite ist leer. Füge links Komponenten hinzu.</p>
            )}
          </div>
        </main>

        {/* Right: properties */}
        <aside className="border-l overflow-y-auto" style={{ borderColor: "var(--mv-border)" }}>
          {selected ? (
            <BlockPropertiesPanel block={selected} idx={selectedIdx} onChange={(patch) => updateBlock(selectedIdx, patch)} platformTypography={platformTypography} />
          ) : (
            <div className="p-4 text-xs opacity-60">Wähle einen Block aus, um seine Eigenschaften zu bearbeiten.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-xs mb-1" style={{ color: "var(--mv-text-muted)" }}>{label}</label>
      {children}
    </div>
  );
}

function ImageUploadField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } finally { setBusy(false); }
  };
  return (
    <div>
      <input className="mv-input text-xs mb-2" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL oder hochladen" />
      <label className="mv-btn-ghost text-xs cursor-pointer w-full justify-center">
        <Upload className="w-3 h-3" /> {busy ? "Lädt..." : "Hochladen"}
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}

function BlockPropertiesPanel({ block, onChange, platformTypography }) {
  const { type, props } = block;
  const platformHeading = platformTypography?.heading_font || "Cormorant Garamond";
  return (
    <div className="p-4">
      <p className="mv-label mb-3">{type}</p>

      {type === "heading" && (
        <>
          <Field label="Text"><input className="mv-input text-sm" value={props.text || ""} onChange={(e) => onChange({ text: e.target.value })} /></Field>
          <Field label="Level">
            <select className="mv-input text-sm" value={props.level || "h2"} onChange={(e) => onChange({ level: e.target.value })}>
              <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option>
            </select>
          </Field>
          <Field label="Ausrichtung">
            <select className="mv-input text-sm" value={props.align || "left"} onChange={(e) => onChange({ align: e.target.value })}>
              <option value="left">Links</option><option value="center">Mitte</option><option value="right">Rechts</option>
            </select>
          </Field>
          <Field label="Schriftart">
            <select
              className="mv-input text-sm"
              data-testid="heading-font-select"
              value={props.font || ""}
              onChange={(e) => onChange({ font: e.target.value || undefined })}
              style={{ fontFamily: `'${props.font || platformHeading}', serif` }}
            >
              <option value="">Standard ({platformHeading})</option>
              {HEADING_FONTS.map((f) => (
                <option key={f.value} value={f.value} style={{ fontFamily: `'${f.value}', serif` }}>{f.label} — {f.category}</option>
              ))}
            </select>
          </Field>
        </>
      )}

      {type === "text" && (
        <>
          <Field label="Inhalt">
            <RichTextEditor
              value={props.text}
              onChange={(v) => onChange({ text: v })}
              placeholder="**fett**, *kursiv*, [link](url), Listen, # Überschriften, …"
              minHeight="220px"
              testId="text-rte"
            />
          </Field>
          <Field label="Ausrichtung">
            <select className="mv-input text-sm" value={props.align || "left"} onChange={(e) => onChange({ align: e.target.value })}>
              <option value="left">Links</option><option value="center">Mitte</option><option value="right">Rechts</option>
            </select>
          </Field>
        </>
      )}

      {type === "image" && (
        <>
          <Field label="Bild"><ImageUploadField value={props.src} onChange={(v) => onChange({ src: v })} /></Field>
          <Field label="Alt-Text"><input className="mv-input text-sm" value={props.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
          <Field label="Bildunterschrift"><input className="mv-input text-sm" value={props.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} /></Field>
        </>
      )}

      {type === "image_fullwidth" && (
        <>
          <Field label="Bild"><ImageUploadField value={props.src} onChange={(v) => onChange({ src: v })} /></Field>
          <Field label="Alt-Text"><input className="mv-input text-sm" value={props.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
          <Field label={`Höhe (${props.height || 480}px)`}>
            <input type="range" min="200" max="900" step="20" value={props.height || 480} onChange={(e) => onChange({ height: parseInt(e.target.value, 10) })} className="w-full" />
          </Field>
          <Field label="Overlay-Titel"><input className="mv-input text-sm" value={props.overlay_title || ""} onChange={(e) => onChange({ overlay_title: e.target.value })} /></Field>
          <Field label="Overlay-Untertitel"><input className="mv-input text-sm" value={props.overlay_subtitle || ""} onChange={(e) => onChange({ overlay_subtitle: e.target.value })} /></Field>
        </>
      )}

      {type === "button" && (
        <>
          <Field label="Label"><input className="mv-input text-sm" value={props.label || ""} onChange={(e) => onChange({ label: e.target.value })} /></Field>
          <Field label="URL"><input className="mv-input text-sm" value={props.url || ""} onChange={(e) => onChange({ url: e.target.value })} /></Field>
        </>
      )}

      {type === "hero" && (
        <>
          <Field label="Titel"><input className="mv-input text-sm" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><input className="mv-input text-sm" value={props.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
          <Field label="CTA Label"><input className="mv-input text-sm" value={props.cta_label || ""} onChange={(e) => onChange({ cta_label: e.target.value })} /></Field>
          <Field label="CTA URL"><input className="mv-input text-sm" value={props.cta_url || ""} onChange={(e) => onChange({ cta_url: e.target.value })} /></Field>
          <Field label="Hintergrundbild"><ImageUploadField value={props.image} onChange={(v) => onChange({ image: v })} /></Field>
        </>
      )}

      {type === "spacer" && (
        <Field label="Höhe (px)"><input type="number" className="mv-input text-sm" value={props.height || 32} onChange={(e) => onChange({ height: parseInt(e.target.value) || 0 })} /></Field>
      )}

      {type === "testimonials" && (
        <Field label="Stimmen">
          <div className="space-y-2">
            {(props.items || []).map((it, i) => (
              <div key={i} className="p-2 rounded border" style={{ borderColor: "var(--mv-border)" }}>
                <input className="mv-input text-xs mb-1" placeholder="Zitat" value={it.quote || ""} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, quote: e.target.value }; onChange({ items: arr });
                }} />
                <input className="mv-input text-xs" placeholder="Autor:in" value={it.author || ""} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, author: e.target.value }; onChange({ items: arr });
                }} />
                <button onClick={() => onChange({ items: props.items.filter((_, j) => j !== i) })} className="text-[10px] text-red-500 mt-1">Entfernen</button>
              </div>
            ))}
            <button onClick={() => onChange({ items: [...(props.items || []), { quote: "", author: "" }] })} className="mv-btn-ghost text-xs w-full"><Plus className="w-3 h-3" /> Hinzufügen</button>
          </div>
        </Field>
      )}

      {type === "faq" && (
        <Field label="Fragen">
          <div className="space-y-2">
            {(props.items || []).map((it, i) => (
              <div key={i} className="p-2 rounded border" style={{ borderColor: "var(--mv-border)" }}>
                <input className="mv-input text-xs mb-1" placeholder="Frage" value={it.question || ""} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, question: e.target.value }; onChange({ items: arr });
                }} />
                <textarea className="mv-input text-xs" placeholder="Antwort" value={it.answer || ""} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, answer: e.target.value }; onChange({ items: arr });
                }} />
                <button onClick={() => onChange({ items: props.items.filter((_, j) => j !== i) })} className="text-[10px] text-red-500 mt-1">Entfernen</button>
              </div>
            ))}
            <button onClick={() => onChange({ items: [...(props.items || []), { question: "", answer: "" }] })} className="mv-btn-ghost text-xs w-full"><Plus className="w-3 h-3" /> Hinzufügen</button>
          </div>
        </Field>
      )}
    </div>
  );
}
